import React, { useRef, useState, useEffect } from "react";
import SendIcon from "../../../../../../assets/svgs/send_icon.svg";
import { LiveChatStyles as styles } from "./chat.tailwind";
import TextArea from "../../../../../shared/Input/TextArea/TextArea";
import ChatMessage from "./ChatMessage";
import EmojiPicker from "emoji-picker-react";
import emoji_icon from '../../../../../../assets/svgs/emoji_icon.svg';
import attachment_icon from "../../../../../../assets/svgs/attachment_live.svg";
import UploadMediaDialog from "./UploadMediaSection";
import { connect, useSelector } from "react-redux";
import { sendMessage } from "../../../../../../redux/slices/liveGameSlice";
import { LIVE_GAME_CHAT_CONTENT_TYPE } from "../../../../../../lib/constants/globalConstant";
import { getCurrentTimestamps, getTimeStamps } from "../../../../../../lib/utils/dateAndTimeFormatting";
import { storage } from "../../../../../../config/storage";
// import MobileChatInput from "./MobileChatInput";
import ChatInputFieldModal from "../../../../../shared/Modals/ChatInputFieldModal/ChatInputFieldModal";
import LiveChatSkeleton from "./Skeleton/LiveChatSkeleton/LiveChatSkeleton";

const LiveChat = ({ gameId, messages, handleNewMessage, sendMsg, replayedMessage, handleReplayCommentary }) => {
  const messagesEndRef = useRef(null); // Ref to track the end of messages
  const emojiPickerRef = useRef(null);
  const uploadMediaRef = useRef(null);
  // const mediaDialogRef = useRef(null);
  const { gameDetails } = useSelector(state => state.scorer);

  const { user } = useSelector((state) => state.user);
  const { sendingMessage, liveGameMedia, uploadingMedia, messageLoading } = useSelector((state) => state.liveGame);
  const { pageNumber } = liveGameMedia;
  // const { universityId } = JSON.parse(localStorage?.getItem('universityId'));
  const universityId = storage.fetchFromLocalStorage.universityId();
  // console.log(universityId, 'uniId');


  const [inputValue, setInputValue] = useState(""); // State to store input value
  const [textAreaRows, setTextAreaRows] = useState(1);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showUploadMediaDialog, setShowUploadMediaDialog] = useState(false);
  const [triggerSave, setTriggerSave] = useState(false); // State to trigger save in UploadMediaDialog
  const [attachmentURLs, setAttachmentURLs] = useState(null); // State to receive result from UploadMediaDialog
  // const [showMobileInput, setShowMobileInput] = useState(false);
  const [showModal, setShowModal] = useState(false); // State to control modal
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 500); // Check screen size

  // Update view on window resize
  // const handleInputClick = (e) => {
  //   if (isMobileView) {
  //     e.target.blur();
  //     setShowModal(true);
  //   }
  // }

  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value);

    if (e.target.value?.length === 0) {
      setTextAreaRows(1);
    }
  };

  const clearInput = () => {
    setInputValue("");
  };

  // Handle emoji selection
  const handleEmojiClick = (emojiObject) => {
    setInputValue((prev) => prev + emojiObject.emoji); // Append the emoji to the input value
  };

  // Handle sending a message
  const handleSendMessage = async () => {
    if (showModal) {
      setShowModal(false);  // Ensure modal closes after sending
    }
    const hasText = inputValue.trim().length > 0;

    // Reset attachmentURLs and trigger save if media dialog is open
    if (showUploadMediaDialog) {
      setAttachmentURLs(null); // Clear previous result
      setTriggerSave(true); // Trigger save in UploadMediaDialog
    } else {
      setTriggerSave(false); // Ensure it’s reset if no dialog
      await processMessage(hasText, null); // Process immediately if no media
    }
  };

  // Process the message once attachmentURLs is available or if no media
  const processMessage = async (hasText, mediaURLs) => {

    const hasMedia = mediaURLs && mediaURLs.length > 0;

    if (!hasText && !hasMedia || sendingMessage) return;

    let contentType;
    if (hasText && hasMedia) {
      contentType = LIVE_GAME_CHAT_CONTENT_TYPE.BOTH_TEXT_MEDIA;
    } else if (hasText) {
      contentType = LIVE_GAME_CHAT_CONTENT_TYPE.TEXT;
    } else {
      contentType = mediaURLs.length >= 1
        ? LIVE_GAME_CHAT_CONTENT_TYPE.MEDIA
        : mediaURLs[0].contentType;
    }

    const data = {
      gameId,
      messageText: hasText ? inputValue.trim() : "",
      contentType,
      attachmentURLs: hasMedia ? mediaURLs : null,
      universityId: universityId,
    };
    // console.log('data: ', data);

    try {
      // await handleNewMessage({
      //   ...data,
      //   userId: user?.userDetails?._id,
      //   userNickName: user?.userDetails?.userNickName,
      //   userProfileImage: user?.userDetails?.profileImageUrl
      // });

      clearInput();
      setShowUploadMediaDialog(false);
      setTriggerSave(false); // Reset trigger
      // console.log('api called');

      const response = await sendMsg(data);
      if (sendMessage.fulfilled.match(response)) {
        data._id = response?.payload?.chatId;
      }

      await handleNewMessage({
        ...data,
        userId: user?.userDetails?._id,
        userNickName: user?.userDetails?.userNickName,
        userProfileImage: user?.userDetails?.profileImageUrl
      });

    } catch (error) {
      console.error('Message sending failed:', error);
    }
  };

  // Watch for attachmentURLs updates from UploadMediaDialog
  useEffect(() => {
    // console.log(attachmentURLs, triggerSave);

    if (triggerSave && attachmentURLs !== null) {
      processMessage(inputValue.trim().length > 0, attachmentURLs);
    }
  }, [attachmentURLs, triggerSave]);

  // Handle key press in the text area
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && e.shiftKey && inputValue?.length > 0) {
      setTextAreaRows(prev => {
        if (prev < 2) {
          return (prev + 1);
        }

        return prev;
      })
    }

    // If Enter is pressed without Shift, Ctrl, or Alt, send the message
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault(); // Prevent default Enter behavior (new line)
      handleSendMessage(); // Send the message on Enter press
    }
  };

  useEffect(() => {
    // Scroll to the bottom whenever messages change    
    if (messagesEndRef.current && !replayedMessage) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    // This useEffect is for closing the emoji picker and attachment menu when clicking outside and focusing out the input field
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false); // Close the emoji picker
      }

      // if (mediaDialogRef.current && !mediaDialogRef.current.contains(event.target)) {
      //   setShowUploadMediaDialog(false); // Close the emoji picker
      // }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update view on window resize
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 500);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

    if(messageLoading) {
      return (
        <div className={styles.chatContainer}>
         <LiveChatSkeleton/>
        </div>
      )
    }

  return (
    <div className={styles.chatContainer}>
      {/* All the messages */}
      <div className={styles.chatMessages} ref={messagesEndRef}>
        {messages.map((message, index) => (
          <ChatMessage key={index} message={message} handleReplayCommentary={handleReplayCommentary} />
        ))}
      </div>

      {/* Input Area aligned to the right */}
      {!((getTimeStamps(gameDetails?.matchStartDatetime) > getCurrentTimestamps())) && (
        <div className={styles.bottom_send_message_wrpr}>
          <div className={styles.input_field_wrpr}>
            {/* text-area input */}
            <TextArea
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="Type a message"
              onKeyDown={handleKeyPress}
              rows={textAreaRows}
              className={"text-[12px] screen-500:text-[14px] font-kanit"}
              background="transparent"
              color="white"
              placeholderColor="white"
              autoFocus={false}
              // onClick={(e) => handleInputClick(e)}
            />

            {/* Buttons */}
            <div className={styles.buttonContainer}>
              {/* Attachment-icon button */}
              <button className={styles.uploadBtn} onClick={() => { if (!uploadingMedia) { setShowUploadMediaDialog((prev) => !prev) } }} >
                <img src={attachment_icon} alt="Upload Icon" className="w-full h-full" />
              </button>

              {/* Emoji-icon button */}
              <button className={styles.emojiButton} onClick={() => setShowEmojiPicker((prev) => !prev)} >
                <img src={emoji_icon} alt="Emoji Icon" className="w-full h-full" />
              </button>
            </div>
          </div>

          {/* Send Button */}
          <button onClick={handleSendMessage} className={`${styles.sendButton}`} disabled={uploadingMedia || sendingMessage}>
            <img src={SendIcon} alt="Send Icon" className="w-full h-full" />
          </button>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div className={styles.attachmentAndEmojiMenu} ref={emojiPickerRef}>
              <EmojiPicker onEmojiClick={handleEmojiClick} />
            </div>
          )}

          {/* Media Dialog */}
          {showUploadMediaDialog && (
            <UploadMediaDialog
              ref={uploadMediaRef}
              gameId={gameId}
              uploadFile={showUploadMediaDialog}
              setUploadFile={setShowUploadMediaDialog}
              handleSendMessage={handleSendMessage}
              pageNumber={pageNumber}
              triggerSave={triggerSave}
              setTriggerSave={setTriggerSave}
              setAttachmentURLs={setAttachmentURLs}
            // handleNewMessage={handleNewMessage}
            />
          )}
        </div>
      )}

      {/* <div className="block screen-500:hidden">
        <MobileChatInput
          setShowMobileInput={setShowMobileInput}
          showMobileInput={showMobileInput}
          value={inputValue}
          onChange={handleInputChange}
        />
      </div> */}
      {showModal && isMobileView &&
        <ChatInputFieldModal
          isOpen={showModal}
          inputValue={inputValue}
          handleSendMessage={handleSendMessage}
          onChange={handleInputChange}
          rows={textAreaRows}
          onKeyDown={handleKeyPress}
          onClose={() => setShowModal(false)}
        />
      }
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  sendMsg: (data) => dispatch(sendMessage(data)),
});

export default connect(null, mapDispatchToProps)(React.memo(LiveChat));